import { useEffect, useState } from "react";

interface CookieOptions {
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None" | string;
}

interface UseCookiesReturn {
  cookies: { [key: string]: string };
  getCookie: (name: string) => string | undefined;
  setCookie: (name: string, value: string, options?: CookieOptions) => void;
  removeCookie: (name: string, options?: CookieOptions) => void;
}

const parseCookies = (): { [key: string]: string } => {
  if (typeof document === "undefined") return {};
  return document.cookie
    .split("; ")
    .reduce((acc: { [key: string]: string }, cookie: string) => {
      const [name, ...rest] = cookie.split("=");
      const value = rest.join("=");
      acc[name] = decodeURIComponent(value);
      return acc;
    }, {});
};

export function useCookies(): UseCookiesReturn {
  const [cookies, setCookies] = useState<{ [key: string]: string }>(() =>
    parseCookies()
  );

  const setCookie = (
    name: string,
    value: string,
    options: CookieOptions = {}
  ): void => {
    let cookieStr = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (options.expires) {
      if (typeof options.expires === "number") {
        const date = new Date();
        date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
        cookieStr += `; expires=${date.toUTCString()}`;
      } else if (options.expires.toUTCString) {
        cookieStr += `; expires=${options.expires.toUTCString()}`;
      }
    }

    cookieStr += `; path=${options.path || "/"}`;
    if (options.domain) cookieStr += `; domain=${options.domain}`;
    cookieStr += `; samesite=${options.sameSite || "None"}`;
    cookieStr += "; secure";

    document.cookie = cookieStr;
    setCookies(parseCookies());
  };

  const getCookie = (name: string): string | undefined => cookies[name];

  const removeCookie = (name: string, options: CookieOptions = {}): void => {
    setCookie(name, "", { ...options, expires: -1 });
  };

  return { cookies, getCookie, setCookie, removeCookie };
}

export function useCookieState<T>(
  key: string,
  initialValue: T
): [T, (value: T) => void] {
  const { cookies, setCookie } = useCookies();
  const value = (cookies[key] as T) || initialValue;

  const setValue = (newValue: T) => {
    setCookie(key, String(newValue));
  };

  useEffect(() => {
    if (!cookies[key]) {
      setCookie(key, String(initialValue));
    }
  }, [initialValue, key, cookies, setCookie]);

  return [value, setValue];
}
